import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Upload, Check } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import FundCriteria from "@/components/FundCriteria";
import { fundApi, formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";

const URGENCY = [
    { value: "niedrig", label: "Niedrig" },
    { value: "normal", label: "Normal" },
    { value: "hoch", label: "Hoch" },
    { value: "akut", label: "Akut" },
];

export default function FundApply() {
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        pigeon_name: "",
        story: "",
        urgency: "normal",
        estimated_cost_eur: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        location: "",
    });
    const [photos, setPhotos] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [consent, setConsent] = useState(false);

    function update(key, value) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function submit(e) {
        e.preventDefault();
        if (!consent) return toast.error("Bitte Einwilligung bestätigen");
        if (!form.pigeon_name || !form.story || !form.contact_name || !form.contact_email) {
            return toast.error("Bitte Pflichtfelder ausfüllen");
        }
        setSubmitting(true);
        try {
            const compressedPhotos = await Promise.all(
                photos.map((f) => (f.type?.startsWith("image/") ? compressImage(f) : Promise.resolve(f)))
            );
            const compressedInvoices = await Promise.all(
                invoices.map((f) => (f.type?.startsWith("image/") ? compressImage(f) : Promise.resolve(f)))
            );
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            compressedPhotos.forEach((f) => fd.append("photos", f));
            compressedInvoices.forEach((f) => fd.append("invoices", f));
            await fundApi.apply(fd);
            setSubmitted(true);
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    }

    if (submitted) {
        return (
            <SiteLayout>
                <div className="max-w-2xl mx-auto px-4 py-32 text-center" data-testid="apply-success">
                    <Check size={32} className="text-emerald-300 mx-auto mb-6" />
                    <h1 className="font-display text-4xl mb-4">Antrag eingegangen</h1>
                    <p className="text-[#94a3b8] mb-10 leading-relaxed">
                        Wir prüfen jeden Antrag persönlich und melden uns innerhalb weniger Tage
                        per E-Mail bei dir.
                    </p>
                    <Link to="/himmelsfonds" className="btn-secondary inline-flex">
                        <ArrowLeft size={14} /> Zurück zum Fonds
                    </Link>
                </div>
            </SiteLayout>
        );
    }

    return (
        <SiteLayout>
            <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <Link to="/himmelsfonds" className="inline-flex items-center gap-2 text-[#94a3b8] hover:text-[#f0c674] text-sm mb-10">
                    <ArrowLeft size={14} /> Zurück zum Fonds
                </Link>
                <div className="label mb-3">Himmelsfond</div>
                <h1 className="font-display text-4xl sm:text-5xl mb-4">Hilfe beantragen</h1>
                <p className="text-[#94a3b8] mb-10 leading-relaxed">
                    Bitte beschreibe den Fall so genau wie möglich. Alle Felder mit{" "}
                    <span className="text-[#f0c674]">*</span> sind Pflicht.
                </p>

                <div className="mb-12">
                    <FundCriteria showLinkToTransparency={true} />
                </div>

                <form onSubmit={submit} className="space-y-5" data-testid="apply-form">
                    <Field label="Name des Falles" required>
                        <input value={form.pigeon_name} onChange={(e) => update("pigeon_name", e.target.value)} className="input-field" required data-testid="apply-name" />
                    </Field>
                    <Field label="Beschreibung des Falls" required>
                        <textarea
                            value={form.story}
                            onChange={(e) => update("story", e.target.value)}
                            className="input-field"
                            rows={5}
                            required
                            data-testid="apply-story"
                            placeholder="Verletzung, Symptome, Behandlung, Dringlichkeit…"
                        />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <Field label="Dringlichkeit" required>
                            <select
                                value={form.urgency}
                                onChange={(e) => update("urgency", e.target.value)}
                                className="input-field"
                                data-testid="apply-urgency"
                            >
                                {URGENCY.map((u) => (
                                    <option key={u.value} value={u.value} className="bg-[#0c1222]">
                                        {u.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Geschätzte Tierarztkosten in €">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.estimated_cost_eur}
                                onChange={(e) => update("estimated_cost_eur", e.target.value)}
                                className="input-field"
                                data-testid="apply-cost"
                            />
                        </Field>
                    </div>
                    <Field label="Ort / Stadt">
                        <input value={form.location} onChange={(e) => update("location", e.target.value)} className="input-field" data-testid="apply-location" />
                    </Field>

                    <Field label="Bilder des Falles (bis 5)">
                        <FileDrop files={photos} setFiles={setPhotos} accept="image/*" testid="apply-photos" />
                    </Field>
                    <Field label="Rechnungen / Atteste (bis 5)">
                        <FileDrop files={invoices} setFiles={setInvoices} accept="image/*,application/pdf" testid="apply-invoices" />
                    </Field>

                    <div className="border-t border-white/5 pt-6">
                        <div className="label mb-3">Deine Kontaktdaten</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Dein Name" required>
                                <input value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className="input-field" required data-testid="apply-contact-name" />
                            </Field>
                            <Field label="E-Mail" required>
                                <input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} className="input-field" required data-testid="apply-contact-email" />
                            </Field>
                        </div>
                        <Field label="Telefon (optional)">
                            <input value={form.contact_phone} onChange={(e) => update("contact_phone", e.target.value)} className="input-field" data-testid="apply-contact-phone" />
                        </Field>
                    </div>

                    <label className="flex items-start gap-3 text-sm text-[#94a3b8] leading-relaxed">
                        <input
                            type="checkbox"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            className="mt-1 accent-[#f0c674]"
                            data-testid="apply-consent"
                        />
                        <span>
                            Ich willige ein, dass meine Angaben zur Prüfung und Bearbeitung des
                            Antrags gespeichert werden. Details siehe{" "}
                            <Link to="/datenschutz" className="text-[#f0c674] underline">
                                Datenschutzerklärung
                            </Link>
                            .
                        </span>
                    </label>

                    <button type="submit" disabled={submitting} className="btn-primary" data-testid="apply-submit">
                        {submitting ? "Wird gesendet…" : "Antrag absenden"}
                    </button>
                </form>
            </section>
        </SiteLayout>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-sm text-[#94a3b8] mb-2">
                {label} {required && <span className="text-[#f0c674]">*</span>}
            </span>
            {children}
        </label>
    );
}

function FileDrop({ files, setFiles, accept, testid }) {
    function add(e) {
        const list = Array.from(e.target.files || []);
        setFiles((cur) => [...cur, ...list].slice(0, 5));
    }
    function remove(idx) {
        setFiles((cur) => cur.filter((_, i) => i !== idx));
    }
    return (
        <div>
            <label
                htmlFor={testid}
                className="cursor-pointer flex items-center gap-3 input-field hover:border-[#f0c674]/40"
            >
                <Upload size={16} className="text-[#f0c674]" />
                <span className="text-sm text-[#94a3b8]">
                    {files.length > 0 ? `${files.length} Datei(en) ausgewählt` : "Dateien auswählen"}
                </span>
            </label>
            <input id={testid} type="file" multiple accept={accept} onChange={add} className="hidden" data-testid={testid} />
            {files.length > 0 && (
                <ul className="mt-3 space-y-1">
                    {files.map((f, i) => (
                        <li key={i} className="text-xs text-[#94a3b8] flex items-center justify-between glass px-3 py-2">
                            <span className="truncate">{f.name}</span>
                            <button type="button" className="text-[#64748b] hover:text-rose-400" onClick={() => remove(i)}>
                                entfernen
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

